import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Loader2, MailCheck, CheckCircle2, Star, ShieldCheck } from "lucide-react";
import { remarketingSignUp } from "@/lib/remarketing";
import { InstallAppPrompt } from "@/components/InstallAppPrompt";

export const Route = createFileRoute("/signup")({
  component: Signup,
  validateSearch: (s: Record<string, unknown>): { next?: string } => ({
    next: typeof s.next === "string" ? s.next : undefined,
  }),

  head: () => ({
    meta: [
      { title: "Skapa konto – Alkoholprovet" },
      { name: "description", content: "Skapa ett konto för att börja öva inför kunskapsprovet för serveringstillstånd." },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
});

function Signup() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { next } = Route.useSearch();
  const destination = next && next.startsWith("/") ? next : "/dashboard";
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) nav({ to: destination as any, replace: true });
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) nav({ to: destination as any, replace: true });
    });
    return () => subscription.unsubscribe();
  }, [nav, destination]);


  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password: pw,
      options: { emailRedirectTo: window.location.origin + destination },
    });
    setLoading(false);
    if (error) {
      setError(error.message);
      return;
    }
    if (data.session) {
      remarketingSignUp("email");
      nav({ to: destination as any });
    } else {
      remarketingSignUp("email");
      setSent(true);
    }
  };

  const signUpOAuth = async (provider: "google" | "apple") => {
    setError(null);
    remarketingSignUp(provider);
    const res = await lovable.auth.signInWithOAuth(provider, { redirect_uri: window.location.origin + destination });
    if (res.error) setError(t("signup.oauthError", { provider: provider === "google" ? "Google" : "Apple" }));
  };

  if (sent) {
    return (
      <div className="container-narrow py-20 max-w-md text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
          <MailCheck className="h-6 w-6" />
        </div>
        <h1 className="mt-5 font-display text-2xl font-bold">{t("signup.sentTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("signup.sentBody", { email })}</p>
        <InstallAppPrompt source="signup" />
      </div>
    );
  }

  const bullets = [t("signup.bullet1"), t("signup.bullet2"), t("signup.bullet3")];

  return (
    <div className="container-narrow py-10 sm:py-16 max-w-md">
      <div className="inline-flex items-center gap-2 rounded-full border border-success/30 bg-success/10 px-3 py-1 text-xs font-medium text-success">
        <Star className="h-3.5 w-3.5 fill-success" /> {t("signup.trustBadge")}
      </div>
      <h1 className="mt-3 font-display text-3xl font-bold">{t("signup.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("signup.sub")}</p>

      <ul className="mt-5 grid gap-2 text-sm">
        {bullets.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 space-y-2">
        <button
          onClick={() => signUpOAuth("google")}
          type="button"
          className="w-full inline-flex items-center justify-center gap-3 rounded-xl border border-border bg-background px-4 py-3 text-sm font-medium hover:bg-secondary"
        >
          {t("signup.google")}
        </button>
        <button
          onClick={() => signUpOAuth("apple")}
          type="button"
          className="w-full inline-flex items-center justify-center gap-3 rounded-xl border border-border bg-foreground px-4 py-3 text-sm font-medium text-background hover:bg-foreground/90"
        >
          {t("signup.apple")}
        </button>
      </div>

      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="flex-1 h-px bg-border" /> {t("signup.or")} <div className="flex-1 h-px bg-border" />
      </div>


      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">{t("signup.email")}</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-input bg-background px-4 py-3" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t("signup.password")}</label>
          <input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full rounded-xl border border-input bg-background px-4 py-3" />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("signup.submit")}
        </button>
        <p className="text-xs text-muted-foreground text-center">
          <Trans
            i18nKey="signup.terms"
            components={{
              1: <Link to="/villkor" className="underline" />,
              3: <Link to="/integritetspolicy" className="underline" />,
            }}
          />
        </p>
      </form>
      <p className="mt-6 text-sm text-muted-foreground text-center">
        {t("signup.haveAccount")} <Link to="/login" className="text-accent hover:underline">{t("signup.loginLink")}</Link>
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-success" /> {t("signup.trustEncrypt")}</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-success" /> {t("signup.trustNoCard")}</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-success" /> {t("signup.trustCancel")}</span>
      </div>
    </div>
  );
}
